import React, { FC, useEffect, useState } from 'react';
import useVisibilitySensor from '../../hooks/use-visibility-sensor';
import translation from '../../languages';
import { CatalogProps } from '../../utils/enhanced-ecommerce';
import imagePathCreator from '../../utils/image-path-creator/image-path-creator';
import isInteractiveStandaloneMaterial from '../../utils/interactive/is-interactive-standalone-material';
import previewsAnimation, { AnimatedEntity } from '../../utils/preview/previews-animation';
import InteractiveBadge from '../interactive/interactive-badge/interactive-badge';
import LmpLazy from '../lmp-lazy/lmp-lazy';
import MaterialClickHandler from '../material-click-handler/material-click-handler';
import PagesInfo from '../pages-info/pages-info';
import recoSliderImpressionHelper from './reco-slider-impression.helper';
import './preview.scss';

type PreviewProps = TranslationProps & {
    height: number;
    item: Material;
    width?: number;
    animation?: boolean;
    lazy?: boolean;
    materialProps?: CatalogProps;
    searchRequestId?: string;
    showPagesInfo?: boolean;
    shouldHideInteractiveBadge?: boolean;
};

const Preview: FC<PreviewProps> = ({ t, shouldHideInteractiveBadge, ...props }) => {
    const {
        item,
        width,
        height,
        animation,
        materialProps = {} as CatalogProps,
        searchRequestId,
        showPagesInfo = true
    } = props;
    let { lazy } = props;
    const isStandalone = isInteractiveStandaloneMaterial(item);
    const isInteractive = Boolean(item?.interactiveId || item?.isBundleInteractive);
    const { isVisible, wasOnceVisible, ref } = useVisibilitySensor({
        threshold: 0.9 // Trigger when 90% of the target is visible
    });
    const [refReady, setRefReady] = useState(false);

    lazy = lazy && !item.bundle;

    const animationSettings = () => {
        if (animation) {
            return {
                onMouseOver: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) =>
                    previewsAnimation.mouseOver(e, item as AnimatedEntity),
                onMouseOut: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) =>
                    previewsAnimation.mouseOut(e, item as AnimatedEntity)
            };
        }
    };

    useEffect(() => {
        if (isVisible && !wasOnceVisible && materialProps.internalPath === 'hp_reco_v3') {
            recoSliderImpressionHelper.track(item, materialProps);
        }
    }, [isVisible, materialProps.internalPath]);

    const formImage = () =>
        imagePathCreator.optimizedImg({ preview: item.firstPreviewImage?.plain }, { h: height, q: 95 });

    const formStyles = () => ({
        backgroundImage: `url(${formImage()})`,
        backgroundPosition: 'top',
        height: `${height}px`,
        width: `${width}px`
    });

    const formPreview = () => (
        <MaterialClickHandler item={item} materialProps={materialProps} searchRequestId={searchRequestId}>
            <div
                {...animationSettings()}
                className='preview'
                style={formStyles()}
                ref={(element) => {
                    if (element) {
                        ref.current = element;
                        setRefReady(true);
                    }
                }}
            >
                {isInteractive && refReady && !shouldHideInteractiveBadge && (
                    <InteractiveBadge
                        isStandalone={isStandalone}
                        width={ref.current?.getBoundingClientRect()?.width || width}
                    />
                )}
                {showPagesInfo && !isStandalone && <PagesInfo item={item} />}
            </div>
        </MaterialClickHandler>
    );

    return (
        <>
            {lazy && (
                <LmpLazy
                    className='preview-container'
                    content={formPreview()}
                    debounce={false}
                    height={height}
                    offset={100}
                    width={width}
                />
            )}
            {!lazy && formPreview()}
        </>
    );
};

const translate = translation('common');

export default translate(Preview);
